import { useState, type JSX } from 'react'
import type { CashForecastWeek, CashPositionMonth } from '../api'
import { monthLabel, yen } from '../format'
import { CHART_INK, SERIES } from '../charts/palette'
import { compactYen, domainFor, monthLabelY, plotValue, ticksFor, yFor } from '../charts/scale'
import { NoDataZone } from '../charts/NoDataZone'
import {
  copyIsMissing,
  defaultWeekIndex,
  gapAt,
  headline,
  nowLine,
  snapshotLine,
  type LinePoint,
} from '../charts/forecastVsActual'
import { monthsAfter } from '../timeWindow'
import { SourceNote } from './FreshnessStatus'

// "Forecast vs actual": one saved weekly copy of the cash forecast, laid over
// what has happened since (month-end actuals) and what the forecast says now.
// The question it answers is "how far off were we since week X?".
//
// ALWAYS THE GOSPEL SERIES. The copies were saved from the GOSPEL-payroll cash
// position, so the lines they are compared against come from the same series
// (cashPositionByMonthGospelAp), whatever the page's payroll source.
//
// ITS OWN PIPELINE SWITCH. Unlike the other forecast charts, this card does not
// follow the page's switch: it compares one forecast with another, and both
// sides are always on the same basis because one switch drives both. Off by
// default, like the page.
//
// NOTHING MOVES WHEN YOU PICK A WEEK. The y axis is fixed over every copy on
// both bases, the headline and legend keep a reserved height, and the chips sit
// under the plot, so clicking through weeks changes only the yellow line.

const W = 720
const H = 274
const PAD = { top: 18, right: 16, bottom: 48, left: 62 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
const LABEL_Y = monthLabelY(PAD.top + PLOT_H)
const SURFACE = '#272C47'

export const FVA_EMPTY = 'Weekly forecast copies start on the first Monday after this ships.'
const BASIS_ON = 'With weighted pipeline: every line adds deals not yet signed.'
const BASIS_OFF = 'Booked money only: every line leaves out deals not yet signed.'
const REBUILT_NOTE =
  "Rebuilt from that week's plans and today's booked invoices, so it may understate the miss."

/** 2026-10-12 → 12 Oct. */
const dayMonth = (date: string): string =>
  `${Number(date.slice(8, 10))} ${monthLabel(date).split(' ')[0] ?? ''}`
/** 2026-10-12 → 12 Oct 2026. */
const fullDate = (date: string): string => `${Number(date.slice(8, 10))} ${monthLabel(date)}`

const signedCompact = (value: number): string =>
  value === 0 ? compactYen(0) : `${value < 0 ? '−' : '+'}${compactYen(Math.abs(value))}`

type Pt = { index: number; value: number }

/**
 * Text whose length changes with the chosen week or the switch, in a slot sized
 * by the longest text it can show (stacked, hidden, in the same grid cell), so
 * the slot never grows or wraps differently at any width and nothing below it
 * moves.
 */
function StableText({ text, longest }: { text: string; longest: string }): JSX.Element {
  return (
    <span className="fva-slot">
      <span className="fva-slot-sizer" aria-hidden="true">
        {longest}
      </span>
      <span>{text}</span>
    </span>
  )
}

const longestOf = (texts: string[]): string =>
  texts.reduce((a, b) => (b.length > a.length ? b : a), '')

/** Split points into runs of neighbouring months, so a missing month breaks the line. */
function runs(points: Pt[]): Pt[][] {
  const out: Pt[][] = []
  for (const p of points) {
    const run = out.at(-1)
    const prev = run?.at(-1)
    if (run !== undefined && prev !== undefined && p.index === prev.index + 1) run.push(p)
    else out.push([p])
  }
  return out
}

function CardHeader({
  showPipeline,
  onShowPipelineChange,
  sourceNote,
  withSwitch,
}: {
  showPipeline: boolean
  onShowPipelineChange: (next: boolean) => void
  sourceNote: string | null
  withSwitch: boolean
}): JSX.Element {
  return (
    <header className="chart-title-row">
      <div className="chart-title">
        <h2>Forecast vs actual</h2>
        {withSwitch && (
          <p className="chart-sub fva-basis">
            <StableText
              text={showPipeline ? BASIS_ON : BASIS_OFF}
              longest={longestOf([BASIS_ON, BASIS_OFF])}
            />
          </p>
        )}
        <SourceNote text={sourceNote} />
      </div>
      {withSwitch && (
        <div className="chart-switches">
          {/* The page's switch markup, so it looks the same, but this card's
              own state: it never changes the other charts. */}
          <div className="plan-control">
            <button
              type="button"
              role="switch"
              aria-checked={showPipeline}
              className={`plan-switch${showPipeline ? ' on' : ''}`}
              onClick={() => onShowPipelineChange(!showPipeline)}
              title="Add or remove weighted pipeline (deals not yet signed) in this card's lines. Applies to this card only."
            >
              <span className="switch-track" aria-hidden="true">
                <span className="switch-thumb" />
              </span>
              Pipeline
            </button>
          </div>
        </div>
      )}
    </header>
  )
}

export function ForecastVsActualChart({
  weeks,
  months,
  sourceNote,
  windowEnd,
  now,
}: {
  /** Saved weekly copies, newest first (data.cashForecastWeeks). */
  weeks: CashForecastWeek[]
  /** Always the GOSPEL series (data.cashPositionByMonthGospelAp). */
  months: CashPositionMonth[]
  sourceNote?: string | null
  /** Last month (YYYY-MM-01) of the page's time window; see NoDataZone. */
  windowEnd: string
  /** For tests; defaults to the current time. */
  now?: Date
}): JSX.Element {
  const [showPipeline, setShowPipeline] = useState(false)
  const [chosenWeek, setChosenWeek] = useState<string | null>(null)
  const [hover, setHover] = useState<number | null>(null)

  if (weeks.length === 0) {
    return (
      <section className="panel chart-panel fva">
        <CardHeader
          showPipeline={showPipeline}
          onShowPipelineChange={setShowPipeline}
          sourceNote={sourceNote ?? null}
          withSwitch={false}
        />
        <p className="notice">{FVA_EMPTY}</p>
      </section>
    )
  }

  // The chosen week by date, so a refresh that brings a new copy keeps the
  // reader on the week they picked.
  const chosenIndex = chosenWeek === null ? -1 : weeks.findIndex((w) => w.weekOf === chosenWeek)
  const weekIndex = chosenIndex >= 0 ? chosenIndex : (defaultWeekIndex(weeks) ?? 0)
  const week = weeks[weekIndex] ?? weeks[0]
  if (week === undefined) return <></> // unreachable: weeks is non-empty
  const rebuilt = week.origin === 'rebuilt'
  const nameOf = (w: CashForecastWeek): string =>
    `Forecast on ${dayMonth(w.weekOf)}${w.origin === 'rebuilt' ? ' (rebuilt)' : ''}`
  const copyName = nameOf(week)
  // The longest legend label and headline any week (on either basis) can
  // produce, which size their slots.
  const longestName = longestOf(weeks.map(nameOf))
  const longestHeadline = longestOf(
    weeks.flatMap((w) => [headline(w, months, true), headline(w, months, false)]),
  )

  const newest = weeks[0] ?? week
  const missing = copyIsMissing(weeks, now ?? new Date())

  // The x axis: the months the page asked for, padded to the window end.
  const lastMonth = months.at(-1)?.month ?? null
  const gap = lastMonth === null ? [] : monthsAfter(lastMonth, windowEnd)
  const axis = [...months.map((m) => m.month), ...gap]
  const indexOf = new Map(axis.map((month, index) => [month, index]))
  const place = (points: LinePoint[]): Pt[] =>
    points.flatMap((p) => {
      const index = indexOf.get(p.month)
      return index === undefined ? [] : [{ index, value: p.value }]
    })

  const current = nowLine(months, showPipeline)
  const actualPts = place(current.actual)
  const projectedPts = place(current.projected)
  const copyPts = place(snapshotLine(week, showPipeline))

  // One domain over every copy and both bases, so neither picking a week nor
  // flipping the switch rescales the axis under the reader.
  const { min, max } = domainFor(
    [true, false].flatMap((basis) => [
      ...place(nowLine(months, basis).actual).map((p) => p.value),
      ...place(nowLine(months, basis).projected).map((p) => p.value),
      ...weeks.flatMap((w) => place(snapshotLine(w, basis)).map((p) => p.value)),
    ]),
  )
  const band = axis.length === 0 ? 0 : PLOT_W / axis.length
  const y = (value: number): number => yFor(value, min, max, PAD.top, PLOT_H)
  const centre = (index: number): number => PAD.left + band * index + band / 2
  const path = (pts: Pt[]): string => pts.map((p) => `${centre(p.index)},${y(p.value)}`).join(' ')

  // Today sits between the last ended month and the month in progress.
  const todayIndex = months.findIndex((m) => m.pointKind === 'projected')
  const todayX = todayIndex < 0 ? null : PAD.left + band * todayIndex

  // The gap: between the copy and the balance as it stands now, from the
  // copy's first month, where both exist.
  const gapPts = copyPts.flatMap((p) => {
    const month = axis[p.index]
    const diff = month === undefined ? null : gapAt(month, week, months, showPipeline)
    return diff === null ? [] : [{ index: p.index, was: p.value, now: p.value + diff }]
  })
  const gapAreas = runs(gapPts.map((p) => ({ index: p.index, value: p.was })))
    .filter((run) => run.length > 1)
    .map((run) => {
      const nowOf = (index: number): number => gapPts.find((p) => p.index === index)?.now ?? 0
      const back = [...run].reverse().map((p) => ({ index: p.index, value: nowOf(p.index) }))
      return `${path(run)} ${path(back)}`
    })

  // Direct labels at the right end of the two forecast lines, pushed apart.
  const copyEnd = copyPts.at(-1)
  const nowEnd = projectedPts.at(-1) ?? actualPts.at(-1)
  const copyAbove = copyEnd !== undefined && nowEnd !== undefined && y(copyEnd.value) <= y(nowEnd.value)

  const hoveredMonth = hover === null ? undefined : axis[hover]
  const hoveredRow = hoveredMonth === undefined ? undefined : months.find((m) => m.month === hoveredMonth)
  const hoveredCopy =
    hoveredMonth === undefined ? undefined : week.points.find((p) => p.month === hoveredMonth)
  const hoveredCopyValue =
    hoveredCopy === undefined
      ? null
      : showPipeline
        ? hoveredCopy.balance
        : hoveredCopy.balanceExPipeline
  const hoveredGap =
    hoveredMonth === undefined || plotValue(hoveredCopyValue) === null
      ? null
      : gapAt(hoveredMonth, week, months, showPipeline)
  const tooltipLeft = hover === null ? 0 : Math.min(85, Math.max(15, (centre(hover) / W) * 100))

  return (
    <section className="panel chart-panel fva">
      <CardHeader
        showPipeline={showPipeline}
        onShowPipelineChange={setShowPipeline}
        sourceNote={sourceNote ?? null}
        withSwitch
      />
      <p className="fva-headline">
        <StableText text={headline(week, months, showPipeline)} longest={longestHeadline} />
      </p>
      <ul className="legend fva-legend">
        <li>
          <span className="swatch line" style={{ background: SERIES.blue }} /> Actual
        </li>
        <li>
          <span className="swatch line dashed" /> Forecast now
        </li>
        <li>
          <span className={`swatch line fva-swatch-copy${rebuilt ? ' dotted' : ''}`} />{' '}
          <StableText text={copyName} longest={longestName} />
        </li>
        <li>
          <span className="swatch fva-swatch-gap" /> Gap
        </li>
      </ul>

      {axis.length === 0 ? (
        <p className="notice fva-plot-empty">
          No cash figures in this range to compare the copy against.
        </p>
      ) : (
        <div className="chart-wrap">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="chart"
            role="img"
            aria-label={`Cash on hand by month: month-end actuals as a solid line, today's forecast dashed, and the forecast saved on ${fullDate(week.weekOf)}${rebuilt ? ' (rebuilt)' : ''} as a yellow line, with the gap between them shaded. ${showPipeline ? 'Weighted pipeline is included.' : 'Pipeline is left out.'}`}
            onMouseLeave={() => setHover(null)}
          >
            {gap.length > 0 && lastMonth !== null && (
              <NoDataZone
                x={PAD.left + band * months.length}
                width={band * gap.length}
                top={PAD.top}
                height={PLOT_H}
                lastMonth={lastMonth}
              />
            )}

            {ticksFor(min, max).map((tick) => (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  x2={W - PAD.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke={Math.abs(tick) < 1e-9 ? CHART_INK.axis : CHART_INK.grid}
                />
                <text x={PAD.left - 8} y={y(tick) + 3} textAnchor="end" className="tick">
                  {compactYen(tick)}
                </text>
              </g>
            ))}

            {todayX !== null && (
              <g>
                <line
                  x1={todayX}
                  x2={todayX}
                  y1={PAD.top}
                  y2={PAD.top + PLOT_H}
                  stroke={CHART_INK.axis}
                  strokeDasharray="2 3"
                />
                <text x={todayX + 6} y={PAD.top + 11} className="tick">
                  today
                </text>
              </g>
            )}

            {gapAreas.map((points) => (
              <polygon key={points} points={points} className="fva-gap" />
            ))}

            {runs(actualPts)
              .filter((run) => run.length > 1)
              .map((run) => (
                <polyline
                  key={`actual-${run[0]?.index ?? 0}`}
                  points={path(run)}
                  fill="none"
                  stroke={SERIES.blue}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
            {projectedPts.length > 1 && (
              <polyline
                points={path(projectedPts)}
                fill="none"
                stroke={SERIES.blue}
                strokeWidth={2}
                strokeDasharray="5 4"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
            {copyPts.length > 1 && (
              <polyline
                points={path(copyPts)}
                fill="none"
                stroke={SERIES.yellow}
                strokeWidth={2}
                strokeDasharray={rebuilt ? '2 4' : undefined}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}

            {actualPts.map((p) => (
              <circle
                key={`a-${p.index}`}
                cx={centre(p.index)}
                cy={y(p.value)}
                r={hover === p.index ? 4.5 : 3}
                fill={SERIES.blue}
                stroke={SURFACE}
                strokeWidth={2}
              />
            ))}
            {copyPts.map((p) => (
              <circle
                key={`c-${p.index}`}
                cx={centre(p.index)}
                cy={y(p.value)}
                r={hover === p.index ? 4.5 : 3}
                fill={SURFACE}
                stroke={SERIES.yellow}
                strokeWidth={2}
              />
            ))}

            {copyEnd !== undefined && (
              <text
                x={centre(copyEnd.index) - 8}
                y={y(copyEnd.value) + (copyAbove ? -9 : 15)}
                textAnchor="end"
                className="tick fva-direct"
              >
                {`forecast on ${dayMonth(week.weekOf)}`}
              </text>
            )}
            {nowEnd !== undefined && (
              <text
                x={centre(nowEnd.index) - 8}
                y={y(nowEnd.value) + (copyAbove ? 15 : -9)}
                textAnchor="end"
                className="tick fva-direct"
              >
                {projectedPts.length > 0 ? 'forecast now' : 'actual'}
              </text>
            )}

            {axis.map((month, index) => (
              <text
                key={`x-${month}`}
                x={centre(index)}
                y={LABEL_Y}
                className="tick"
                textAnchor="end"
                transform={`rotate(-35 ${centre(index)} ${LABEL_Y})`}
              >
                {monthLabel(month)}
              </text>
            ))}

            {axis.map((month, index) => (
              <rect
                key={`hit-${month}`}
                x={PAD.left + band * index}
                y={PAD.top}
                width={band}
                height={PLOT_H}
                fill="transparent"
                onMouseEnter={() => setHover(index)}
              />
            ))}

            {hover !== null && (
              <line
                x1={centre(hover)}
                x2={centre(hover)}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
                stroke={CHART_INK.axis}
                strokeWidth={1}
                pointerEvents="none"
              />
            )}
          </svg>

          {hoveredMonth !== undefined && (
            <div className="tooltip fva-tooltip" style={{ left: `${tooltipLeft}%` }}>
              <strong>{monthLabel(hoveredMonth)}</strong>
              {hoveredRow === undefined ? (
                <span className="tooltip-note">No current figure for this month</span>
              ) : hoveredRow.pointKind === 'projected' ? (
                <span>
                  Forecast now{' '}
                  <b>
                    {yen(
                      showPipeline
                        ? hoveredRow.projectedBalance
                        : hoveredRow.projectedBalanceExPipeline,
                    )}
                  </b>
                </span>
              ) : hoveredRow.pointKind === 'missing' ? (
                <span className="tooltip-note">No month-end balance</span>
              ) : (
                <span>
                  Actual <b>{yen(hoveredRow.actualBalance)}</b>
                </span>
              )}
              {plotValue(hoveredCopyValue) !== null ? (
                <span>
                  {copyName} <b>{yen(hoveredCopyValue)}</b>
                </span>
              ) : (
                <span className="tooltip-note">
                  Not in the forecast from {dayMonth(week.weekOf)}
                </span>
              )}
              {hoveredGap !== null && (
                <span className={hoveredGap < 0 ? 'delta-neg' : 'delta-pos'}>
                  Difference <b>{signedCompact(hoveredGap)}</b>
                </span>
              )}
              {rebuilt && plotValue(hoveredCopyValue) !== null && (
                <span className="tooltip-note">{REBUILT_NOTE}</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="fva-weeks">
        <p className="fva-weeks-label" id="fva-weeks-label">
          Forecast saved on
        </p>
        <div className="fva-chips" role="group" aria-labelledby="fva-weeks-label">
          {/* Newest on the right: the API sends newest first. */}
          {[...weeks].reverse().map((w) => {
            const on = w.weekOf === week.weekOf
            const wasRebuilt = w.origin === 'rebuilt'
            return (
              <button
                key={w.weekOf}
                type="button"
                className={`fva-chip${wasRebuilt ? ' rebuilt' : ''}${on ? ' on' : ''}`}
                aria-pressed={on}
                title={
                  wasRebuilt
                    ? `Rebuilt estimate for the week of ${fullDate(w.weekOf)}`
                    : `Saved on Monday ${fullDate(w.weekOf)}`
                }
                onClick={() => {
                  setChosenWeek(w.weekOf)
                  setHover(null)
                }}
              >
                {dayMonth(w.weekOf)}
              </button>
            )
          })}
        </div>
      </div>

      <p className={`fva-foot${missing ? ' stale' : ''}`}>
        {missing
          ? `This week's copy is missing; last copy ${fullDate(newest.weekOf)}`
          : `Last weekly copy: ${fullDate(newest.weekOf)}`}
      </p>
    </section>
  )
}
